/**
 * Where a service error goes. `message` is the operator's (the UI shows it verbatim); `upstream`
 * is the provider's own words on a `502` and goes to the console only, never into the UI: it is
 * what makes an incident answerable without two people, and it is not written for an operator.
 */
import type { TFunction } from 'i18next';

import { isMailboxError, type MailboxError } from '../../mock-data/mailbox-records';

/** The service's error behind a rejection, or `null` when the rejection was not the service
 *  answering (a network failure, a blocked tab): there is no operator message to show then. The
 *  provider's words on a `502` are logged here, once, as the error is taken in. */
export const takeMailboxError = (rejection: unknown): MailboxError | null => {
  if (!isMailboxError(rejection)) return null;
  if (rejection.code === 'upstream_failed' && rejection.upstream) {
    // eslint-disable-next-line no-console -- the one sanctioned place `upstream` is surfaced
    console.error(`[ows-mail] upstream_failed: ${rejection.upstream}`);
  }
  return rejection;
};

/**
 * What to SAY for a mailbox error, wherever one is shown: a refusal for the owner's session (`401`,
 * or no token at all) says to sign in again, since retrying cannot help; a `402` (the main wallet
 * could not pay for a paid create) says so in OUR words, whatever the body says: its code and wording
 * still say "credits" while the money is the main wallet's, and the operator must hear the two facts
 * that decide what they do next, that nothing was created and that the same create works after a
 * top-up; otherwise the service's own message when it has one; a bodiless `403` says the domain is
 * not available to this account (never the caller's "try again" fallback: a 403 does not clear by
 * waiting); otherwise the `fallback`.
 * Never an empty string by accident: the auth refusals can arrive with no message (no token, or a
 * bodiless `401`/`403` from a gateway), and `error?.message ?? fallback` would have shown that `''`.
 */
export const mailboxErrorText = (error: MailboxError | null, t: TFunction, fallback: string): string =>
  error?.code === 'unauthorized'
    ? t('Your session has ended, so sign in again to continue.')
    : error?.code === 'insufficient_credits'
      ? t(
          'Your main wallet does not cover this mailbox, so it was not created and nothing was charged. Add funds, then create it again.',
        )
      : error?.message
        ? error.message
        : error?.code === 'forbidden'
          ? t('Mail on this domain is not available to your account.')
          : fallback;
